import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import {
  FaChartBar,
  FaPaperPlane,
  FaCheckDouble,
  FaEye,
  FaExclamationCircle,
  FaSync,
  FaWhatsapp,
  FaBuilding,
} from "react-icons/fa";
import { db } from "../../firebase";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import { useToast } from "../../components/Toast";
import {
  colors,
  spacing,
  typography,
  radius,
  shadows,
  pageContainer,
  pageTitle,
  pageSubtitle,
} from "../../utils/designTokens";

/**
 * Dashboard avanzado de notificaciones WhatsApp.
 *
 * KPIs:
 *  - Volumen total enviado (rango)
 *  - Tasa de entrega (delivered/sent)
 *  - Tasa de lectura (read/delivered)
 *  - Tasa de fallo (failed/sent)
 *  - Quality rating actual del numero (de meta_health)
 *  - Costos estimados (placeholder hasta tener datos reales por conversation)
 *
 * Filtros:
 *  - Rango de fechas: 7d / 30d / 90d
 *  - Edificio: dropdown de todos los buildings
 *  - Partner: dropdown de partners
 *  - Categoria de template: nueva_expensa, recordatorio_pago, etc.
 *
 * Visualizaciones:
 *  - Cards de KPIs principales arriba.
 *  - Tabla con desglose por template: total enviados / entregados / leidos / fallidos.
 *  - Tabla con desglose por edificio.
 *
 * Datos: lee de la coleccion `messages` (direction=outbound) y `meta_health`
 * (ultimo snapshot de quality rating).
 */

const RANGE_OPTIONS = [
  { id: "7d", label: "Ultimos 7 dias", days: 7 },
  { id: "30d", label: "Ultimos 30 dias", days: 30 },
  { id: "90d", label: "Ultimos 90 dias", days: 90 },
];

const NotificacionesDashboard = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [health, setHealth] = useState(null);
  const [rangeId, setRangeId] = useState("30d");
  const [filterBuilding, setFilterBuilding] = useState("all");
  const [filterPartner, setFilterPartner] = useState("all");
  const [filterTemplate, setFilterTemplate] = useState("all");

  const range = useMemo(() => RANGE_OPTIONS.find((r) => r.id === rangeId) || RANGE_OPTIONS[1], [rangeId]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const sinceDate = new Date(Date.now() - range.days * 24 * 60 * 60 * 1000);

      const [msgsSnap, buildingsSnap, partnersSnap, healthSnap] = await Promise.all([
        getDocs(query(
          collection(db, "messages"),
          where("direction", "==", "outbound"),
          where("createdAt", ">=", sinceDate),
          orderBy("createdAt", "desc"),
          limit(2000)
        )),
        getDocs(collection(db, "building")),
        getDocs(collection(db, "partners")),
        getDocs(query(
          collection(db, "meta_health"),
          orderBy("timestamp", "desc"),
          limit(1)
        )),
      ]);

      setMessages(msgsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setBuildings(buildingsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPartners(partnersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setHealth(healthSnap.empty ? null : healthSnap.docs[0].data());
    } catch (e) {
      console.error("NotificacionesDashboard fetch error:", e);
      const indexHint = e.message?.includes("requires an index")
        ? " (puede faltar un índice compuesto en messages)"
        : "";
      showToast(`Error cargando dashboard${indexHint}`, "error");
    } finally {
      setLoading(false);
    }
  }, [range.days, showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Aplicar filtros sobre los messages
  const filtered = useMemo(() => {
    return messages.filter((m) => {
      if (filterBuilding !== "all" && String(m.buildingId) !== String(filterBuilding)) return false;
      if (filterPartner !== "all") {
        if (filterPartner === "_directo") {
          if (m.partnerId) return false;
        } else if (String(m.partnerId) !== String(filterPartner)) return false;
      }
      if (filterTemplate !== "all" && m.templateKey !== filterTemplate) return false;
      return true;
    });
  }, [messages, filterBuilding, filterPartner, filterTemplate]);

  // KPIs agregados
  const kpis = useMemo(() => {
    const total = filtered.length;
    const sent = filtered.filter((m) => ["sent", "delivered", "read"].includes(m.status)).length;
    const delivered = filtered.filter((m) => ["delivered", "read"].includes(m.status)).length;
    const read = filtered.filter((m) => m.status === "read").length;
    const failed = filtered.filter((m) => m.status === "failed").length;
    const deliveryRate = sent > 0 ? Math.round((delivered / sent) * 100) : null;
    const readRate = delivered > 0 ? Math.round((read / delivered) * 100) : null;
    const failureRate = total > 0 ? Math.round((failed / total) * 100) : null;
    return { total, sent, delivered, read, failed, deliveryRate, readRate, failureRate };
  }, [filtered]);

  // Desglose por template
  const byTemplate = useMemo(() => {
    const groups = new Map();
    filtered.forEach((m) => {
      const k = m.templateKey || "unknown";
      if (!groups.has(k)) groups.set(k, { template: k, total: 0, sent: 0, delivered: 0, read: 0, failed: 0 });
      const g = groups.get(k);
      g.total += 1;
      if (["sent", "delivered", "read"].includes(m.status)) g.sent += 1;
      if (["delivered", "read"].includes(m.status)) g.delivered += 1;
      if (m.status === "read") g.read += 1;
      if (m.status === "failed") g.failed += 1;
    });
    return Array.from(groups.values()).sort((a, b) => b.total - a.total);
  }, [filtered]);

  // Desglose por edificio
  const byBuilding = useMemo(() => {
    const groups = new Map();
    filtered.forEach((m) => {
      const k = String(m.buildingId || "—");
      if (!groups.has(k)) groups.set(k, { buildingId: k, total: 0, delivered: 0, read: 0, failed: 0 });
      const g = groups.get(k);
      g.total += 1;
      if (["delivered", "read"].includes(m.status)) g.delivered += 1;
      if (m.status === "read") g.read += 1;
      if (m.status === "failed") g.failed += 1;
    });
    // Anotar nombre del edificio
    return Array.from(groups.values())
      .map((g) => {
        const b = buildings.find((bb) => String(bb.buildingid) === g.buildingId);
        return { ...g, buildingName: b?.name || `Edif. ${g.buildingId}` };
      })
      .sort((a, b) => b.total - a.total);
  }, [filtered, buildings]);

  // Lista única de templates en uso (para el filtro)
  const templateKeys = useMemo(() => {
    const set = new Set(messages.map((m) => m.templateKey).filter(Boolean));
    return Array.from(set).sort();
  }, [messages]);

  if (loading && messages.length === 0) return <Loader />;

  return (
    <div style={pageContainer}>
      <div style={headerStyle}>
        <div>
          <h1 style={pageTitle}>Dashboard de notificaciones</h1>
          <p style={{ ...pageSubtitle, marginTop: 4, maxWidth: 720 }}>
            Métricas de envíos por WhatsApp segmentables por edificio, partner y plantilla.
            Datos de los últimos {range.days} días.
          </p>
        </div>
        <button onClick={fetchAll} style={ghostBtnStyle}>
          <FaSync size={11} />
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div style={filtersRow}>
        <select value={rangeId} onChange={(e) => setRangeId(e.target.value)} style={selectStyle}>
          {RANGE_OPTIONS.map((r) => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
        <select value={filterBuilding} onChange={(e) => setFilterBuilding(e.target.value)} style={selectStyle}>
          <option value="all">Todos los edificios</option>
          {buildings.map((b) => (
            <option key={b.id} value={b.buildingid}>{b.name || `Edif. ${b.buildingid}`}</option>
          ))}
        </select>
        <select value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)} style={selectStyle}>
          <option value="all">Todos los canales</option>
          <option value="_directo">Directo (sin partner)</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select value={filterTemplate} onChange={(e) => setFilterTemplate(e.target.value)} style={selectStyle}>
          <option value="all">Todas las plantillas</option>
          {templateKeys.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </div>

      {/* KPIs */}
      <div style={kpiRow}>
        <KpiCard icon={FaPaperPlane} label="Enviados" value={kpis.sent} color={colors.primary} bg={colors.primaryLighter} />
        <KpiCard
          icon={FaCheckDouble}
          label="Tasa de entrega"
          value={kpis.deliveryRate === null ? "—" : `${kpis.deliveryRate}%`}
          subValue={`${kpis.delivered} de ${kpis.sent}`}
          color={
            kpis.deliveryRate === null ? colors.gray600 :
            kpis.deliveryRate >= 95 ? colors.success :
            kpis.deliveryRate >= 80 ? colors.warning : colors.error
          }
          bg={
            kpis.deliveryRate === null ? colors.gray100 :
            kpis.deliveryRate >= 95 ? colors.successLight :
            kpis.deliveryRate >= 80 ? colors.warningLight : colors.errorLight
          }
        />
        <KpiCard
          icon={FaEye}
          label="Tasa de lectura"
          value={kpis.readRate === null ? "—" : `${kpis.readRate}%`}
          subValue={`${kpis.read} leyeron`}
          color={kpis.readRate >= 50 ? colors.success : colors.gray600}
          bg={kpis.readRate >= 50 ? colors.successLight : colors.gray100}
        />
        <KpiCard
          icon={FaExclamationCircle}
          label="Tasa de fallo"
          value={kpis.failureRate === null ? "—" : `${kpis.failureRate}%`}
          subValue={`${kpis.failed} fallidos`}
          color={
            kpis.failureRate === null ? colors.gray600 :
            kpis.failureRate <= 1 ? colors.success :
            kpis.failureRate <= 5 ? colors.warning : colors.error
          }
          bg={
            kpis.failureRate === null ? colors.gray100 :
            kpis.failureRate <= 1 ? colors.successLight :
            kpis.failureRate <= 5 ? colors.warningLight : colors.errorLight
          }
        />
      </div>

      {/* Health del numero */}
      {health && (
        <div style={{
          padding: spacing[3],
          background: colors.white,
          borderRadius: radius.md,
          boxShadow: shadows.xs,
          marginBottom: spacing[5],
          display: "flex",
          alignItems: "center",
          gap: spacing[4],
          flexWrap: "wrap",
        }}>
          <FaWhatsapp size={20} color={"#25D366"} />
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
              Número WhatsApp
            </div>
            <div style={{ fontSize: typography.sizes.lg, color: colors.gray900 }}>
              {health.displayPhoneNumber || "—"} ({health.verifiedName || "Habitia"})
            </div>
          </div>
          <QualityBadge rating={health.qualityRating} />
          <div style={{ minWidth: 140 }}>
            <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Tier de mensajería</div>
            <div style={{ fontSize: typography.sizes.base, color: colors.gray900 }}>
              {health.messagingLimitTier || "—"}
            </div>
          </div>
        </div>
      )}

      {/* Desglose por template */}
      {byTemplate.length > 0 && (
        <DataBreakdown
          title="Por plantilla"
          icon={FaWhatsapp}
          rows={byTemplate}
          renderRow={(g) => (
            <>
              <td style={cellMain}>{g.template}</td>
              <td style={cellNumeric}>{g.total}</td>
              <td style={cellNumeric}>{g.sent}</td>
              <td style={cellNumeric}>{g.delivered}</td>
              <td style={cellNumeric}>{g.read}</td>
              <td style={{ ...cellNumeric, color: g.failed > 0 ? colors.error : colors.gray600 }}>
                {g.failed}
              </td>
              <td style={cellNumeric}>
                {g.sent > 0 ? `${Math.round((g.delivered / g.sent) * 100)}%` : "—"}
              </td>
            </>
          )}
          headers={["Plantilla", "Total", "Enviados", "Entregados", "Leídos", "Fallidos", "% entrega"]}
        />
      )}

      {/* Desglose por edificio */}
      {byBuilding.length > 0 && (
        <DataBreakdown
          title="Por edificio"
          icon={FaBuilding}
          rows={byBuilding}
          renderRow={(g) => (
            <>
              <td style={cellMain}>{g.buildingName}</td>
              <td style={cellNumeric}>{g.total}</td>
              <td style={cellNumeric}>{g.delivered}</td>
              <td style={cellNumeric}>{g.read}</td>
              <td style={{ ...cellNumeric, color: g.failed > 0 ? colors.error : colors.gray600 }}>
                {g.failed}
              </td>
              <td style={cellNumeric}>
                {g.total > 0 ? `${Math.round((g.read / g.total) * 100)}%` : "—"}
              </td>
            </>
          )}
          headers={["Edificio", "Total", "Entregados", "Leídos", "Fallidos", "% lectura"]}
        />
      )}

      {filtered.length === 0 && (
        <EmptyState
          icon={FaChartBar}
          title="No hay envíos en el rango seleccionado"
          description="Cuando empiecen a salir notificaciones, los KPIs aparecen acá. Si la WABA está restringida, los datos se van a poblar cuando se levante."
        />
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, subValue, color, bg }) => (
  <div style={{
    padding: `${spacing[4]}px ${spacing[5]}px`,
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    flex: "1 1 220px",
    minWidth: 220,
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  }}>
    <div style={{
      width: 48, height: 48,
      borderRadius: radius.sm,
      background: bg,
      color,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Icon size={20} />
    </div>
    <div>
      <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{label}</div>
      <div style={{ fontSize: typography.sizes["2xl"], fontWeight: 400, color }}>
        {value}
      </div>
      {subValue && (
        <div style={{ fontSize: 11, color: colors.gray400 }}>{subValue}</div>
      )}
    </div>
  </div>
);

const QualityBadge = ({ rating }) => {
  const cfgByRating = {
    GREEN: { label: "Alta", bg: colors.successLight, color: colors.success },
    YELLOW: { label: "Media", bg: colors.warningLight, color: colors.warning },
    RED: { label: "Baja", bg: colors.errorLight, color: colors.error },
    UNKNOWN: { label: "Sin datos", bg: colors.gray100, color: colors.gray600 },
  };
  const cfg = cfgByRating[rating] || cfgByRating.UNKNOWN;
  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Calidad del número</div>
      <span style={{
        display: "inline-block",
        padding: "2px 12px",
        background: cfg.bg,
        color: cfg.color,
        borderRadius: radius.full,
        fontSize: typography.sizes.base,
        marginTop: 2,
      }}>
        {cfg.label}
      </span>
    </div>
  );
};

const DataBreakdown = ({ title, icon: Icon, rows, renderRow, headers }) => (
  <div style={{
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    marginBottom: spacing[5],
    overflow: "hidden",
  }}>
    <div style={{
      padding: `${spacing[3]}px ${spacing[4]}px`,
      borderBottom: `1px solid ${colors.gray200}`,
      display: "flex",
      alignItems: "center",
      gap: spacing[2],
      fontSize: typography.sizes.lg,
      color: colors.gray900,
    }}>
      {Icon && <Icon size={14} color={colors.gray600} />}
      {title}
    </div>
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: typography.sizes.base }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: i === 0 ? "left" : "right",
                  padding: `${spacing[2]}px ${spacing[3]}px`,
                  fontSize: typography.sizes.xs,
                  color: colors.gray600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  background: colors.gray50,
                  borderBottom: `1px solid ${colors.gray200}`,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={idx}
              style={{
                borderBottom: `1px solid ${colors.gray100}`,
                background: idx % 2 === 0 ? colors.white : colors.gray50,
              }}
            >
              {renderRow(row)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: spacing[4],
  gap: spacing[4],
};

const filtersRow = {
  display: "flex",
  gap: spacing[2],
  marginBottom: spacing[4],
  flexWrap: "wrap",
};

const kpiRow = {
  display: "flex",
  gap: spacing[3],
  marginBottom: spacing[5],
  flexWrap: "wrap",
};

const selectStyle = {
  padding: "8px 12px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray900,
  background: colors.white,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  outline: "none",
  fontFamily: "inherit",
  minWidth: 160,
};

const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray700,
  background: colors.white,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const cellMain = {
  textAlign: "left",
  padding: `${spacing[2]}px ${spacing[3]}px`,
  fontSize: typography.sizes.base,
  color: colors.gray900,
};

const cellNumeric = {
  textAlign: "right",
  padding: `${spacing[2]}px ${spacing[3]}px`,
  fontSize: typography.sizes.base,
  color: colors.gray700,
  fontVariantNumeric: "tabular-nums",
};

export default NotificacionesDashboard;
